import { zodResolver } from '@hookform/resolvers/zod';
import { StripeAuthConnectRequestSchema } from '@weft/api-definition';
import { isNotNullOrUndefined } from '@weft/utils';
import { VayuTextField } from 'apps/app/src/components/design/vayu-theme-components/VayuTextField';
import { useStripeConnect } from 'apps/app/src/hooks/requests/use-stripe-connect';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import { get } from 'lodash';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfigurationViewPropertiesBlock } from '../../ConfigurationViewPropertiesBlock';
import { STRIPE_CONFIGURATION_SECTIONS } from '../stripe-configuration-consts/stripe-configuration-sections';
import type { StripeAuthWithSections } from '../stripe-configuration-types/stripe-auth-payload-with-sections-type';

export type AuthConfigurationSectionProps = {
  apiToken: string;
};

export const AuthConfigurationSection: React.FC<AuthConfigurationSectionProps> = ({ apiToken }) => {
  const methods = useForm<StripeAuthWithSections>({
    resolver: zodResolver(StripeAuthConnectRequestSchema),
    defaultValues: { apiToken },
  });
  const { register, setValue, getValues, watch, setError, clearErrors, formState: { errors, isDirty } } = methods;

  const apiTokenInputRegister = register('apiToken');
  const isInEditMode = watch('sectionToEdit') === STRIPE_CONFIGURATION_SECTIONS.auth;

  const mutateAsyncStripeConnect = useStripeConnect();

  const handleCancelClick = () => {
    methods.resetField('apiToken');
  };

  const handleSubmit = async () => {
    const apiTokenValue = getValues('apiToken');
    
    await runMutation(
      mutateAsyncStripeConnect,
      { apiToken: apiTokenValue },
      {
        pending: 'Connecting...',
        success: 'Connected',
        error: 'Failed to connect',
      },
      {
        onSuccess: () => {
          setValue('sectionToEdit', STRIPE_CONFIGURATION_SECTIONS.none);
          clearErrors();
        },
        onError: () => {
          setError(apiTokenInputRegister.name, {
            type: 'manual',
            message: 'Invalid API token',
          });
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <ConfigurationViewPropertiesBlock<StripeAuthWithSections>
        title='Authentication'
        showEditButton
        onCancel={handleCancelClick}
        fieldValue={STRIPE_CONFIGURATION_SECTIONS.auth}
        field='sectionToEdit'
        onSubmit={handleSubmit}
        disableSaveButton={!isDirty}
      >
        <div className='flex w-1/5 flex-col border-0 px-6'>
          <VayuTextField
            label='API key'
            {...apiTokenInputRegister}
            focused={isInEditMode}
            disabled={!isInEditMode}
            defaultValue={getValues('apiToken')}
            placeholder="Text here"
            error={isNotNullOrUndefined(get(errors, apiTokenInputRegister.name))}
            helperText={get(errors, apiTokenInputRegister.name)?.message}
          />
        </div>
      </ConfigurationViewPropertiesBlock>
    </FormProvider>
  );
};